/**
 * AdminHub — sidebar + content layout used inside each top-level admin tab.
 * Mirrors the Stitch sidebar pattern (#635983 active accent, Manrope nav text)
 * and the HubPage card-grid pattern when no section is selected.
 */
import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { T, Icon } from './adminSettingsCommon';

const SIDEBAR_WIDTH = 248;

export default function AdminHub({ title, subtitle, sections, activeKey, onSelect }) {
  const activeSection = sections.find(s => s.key === activeKey);

  return (
    <Box sx={{
      display: 'flex',
      minHeight: 'calc(100vh - 200px)',
      bgcolor: T.bg,
      borderRadius: '0.75rem',
      overflow: 'hidden',
      border: `1px solid ${T.surfaceHigh}`,
    }}>
      {/* Sidebar */}
      <Box sx={{
        width: SIDEBAR_WIDTH, flexShrink: 0,
        bgcolor: T.surface,
        borderRight: `1px solid ${T.surfaceHigh}`,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{
            fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.12em', color: T.outlineVariant,
          }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{
              fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline,
              mt: 0.5, lineHeight: 1.4,
            }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: T.surfaceHigh }} />
        <Box sx={{ flex: 1, p: 1, overflow: 'auto' }}>
          <SidebarItem
            label="Overzicht" iconName="grid_view"
            active={!activeKey}
            onClick={() => onSelect(null)}
          />
          <Divider sx={{ my: 1, mx: 1.5, borderColor: T.surfaceHigh }} />
          {sections.map(section => (
            <SidebarItem
              key={section.key}
              label={section.label}
              iconName={section.icon}
              accent={section.accent}
              active={activeKey === section.key}
              onClick={() => onSelect(section.key)}
            />
          ))}
        </Box>
      </Box>

      {/* Mobile section selector — visible only on xs/sm */}
      <Box sx={{
        display: { xs: 'flex', md: 'none' }, flexDirection: 'column',
        width: '100%',
      }}>
        <Box sx={{
          display: 'flex', gap: 1, p: 1.5, overflowX: 'auto',
          bgcolor: T.surface, borderBottom: `1px solid ${T.surfaceHigh}`,
        }}>
          <ChipNav active={!activeKey} label="Overzicht" iconName="grid_view" onClick={() => onSelect(null)} />
          {sections.map(s => (
            <ChipNav
              key={s.key}
              label={s.label} iconName={s.icon} accent={s.accent}
              active={activeKey === s.key}
              onClick={() => onSelect(s.key)}
            />
          ))}
        </Box>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {activeSection ? (
            <activeSection.component />
          ) : (
            <SectionGrid sections={sections} onSelect={onSelect} />
          )}
        </Box>
      </Box>

      {/* Desktop content */}
      <Box sx={{
        flex: 1, minWidth: 0, p: { md: 4, lg: 5 },
        display: { xs: 'none', md: 'block' },
      }}>
        {activeSection ? (
          <activeSection.component />
        ) : (
          <SectionGrid sections={sections} onSelect={onSelect} />
        )}
      </Box>
    </Box>
  );
}

function SidebarItem({ label, iconName, active, onClick, accent }) {
  const accentColor = accent?.color || T.tertiary;
  const accentBg = accent?.bg || T.tertiaryContainer;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.5, py: 1, borderRadius: '0.5rem',
        cursor: 'pointer', userSelect: 'none',
        bgcolor: active ? 'rgba(99,89,131,0.08)' : 'transparent',
        borderLeft: active ? `3px solid ${accentColor}` : '3px solid transparent',
        transition: 'background-color 0.12s',
        '&:hover': { bgcolor: active ? 'rgba(99,89,131,0.12)' : 'rgba(0,0,0,0.03)' },
      }}
    >
      <Box sx={{
        width: 28, height: 28, borderRadius: '0.4rem', flexShrink: 0,
        bgcolor: active ? accentBg : T.surfaceLow,
        color: active ? accentColor : T.outline,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={iconName} size={16} fill={active} />
      </Box>
      <Typography sx={{
        fontFamily: T.font, fontSize: '0.82rem',
        fontWeight: active ? 700 : 500,
        color: active ? T.text : T.secondary,
        letterSpacing: '0.005em', flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

function ChipNav({ label, iconName, active, onClick, accent }) {
  const accentColor = accent?.color || T.tertiary;
  const accentBg = accent?.bg || T.tertiaryContainer;
  return (
    <Box onClick={onClick} sx={{
      flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75,
      px: 1.25, py: 0.75, borderRadius: '999px', cursor: 'pointer',
      bgcolor: active ? accentBg : T.surfaceLow,
      color: active ? accentColor : T.secondary,
      fontFamily: T.font, fontSize: '0.78rem', fontWeight: active ? 700 : 600,
    }}>
      <Icon name={iconName} size={14} fill={active} />
      {label}
    </Box>
  );
}

function SectionGrid({ sections, onSelect }) {
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)',
      },
      gap: 2,
    }}>
      {sections.map(s => {
        const accent = s.accent || { color: T.primary, bg: T.primaryContainer };
        return (
          <Box
            key={s.key}
            onClick={() => onSelect(s.key)}
            sx={{
              cursor: 'pointer',
              bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5,
              border: `1px solid ${T.surfaceHigh}`,
              transition: 'all 0.15s',
              display: 'flex', flexDirection: 'column', gap: 1.5,
              minHeight: 140,
              '&:hover': {
                boxShadow: '0 8px 24px rgba(45,51,53,0.07)',
                borderColor: `${accent.color}40`,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: '0.625rem',
              bgcolor: accent.bg, color: accent.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name={s.icon} size={24} fill />
            </Box>
            <Box>
              <Typography sx={{
                fontFamily: T.font, fontSize: '0.95rem', fontWeight: 700,
                color: T.text, mb: 0.5,
              }}>
                {s.label}
              </Typography>
              {s.desc && (
                <Typography sx={{
                  fontFamily: T.fontBody, fontSize: '0.75rem',
                  color: T.outline, lineHeight: 1.45,
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {s.desc}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
